import { Component, JSXElement, For, Show, onMount, onCleanup } from 'solid-js';

const SINGLE_BUTTON_THRESHOLD = 2;
import { MODUS_RIGHTPANEL_STYLESHEET } from '../../mc-custom-css/mc-rightpanel.css';
import { injectInRenderContext } from '../util';

/**
 * SolidJS Rightpanel component - Simplified Design
 *
 * Usage Example:
 * ```tsx
 * <Rightpanel
 *   header={
 *     <div>
 *       <h3>My Panel Title</h3>
 *       <button>Action Button</button>
 *     </div>
 *   }
 *   content={<div>Panel content goes here</div>}
 *   footer={{
 *     children: <div>Footer content</div>
 *   }}
 * />
 * ```
 *
 * Note: This component uses `header`, `content`, and `footer` props
 * for JSX content - the host application has full control over the layout.
 */

export interface IRightpanelFooterButtonProps {
  onClick?(event: MouseEvent): void;
  [key: string]: unknown;
}

export interface IRightpanelFooterButton {
  text: string;
  class: string;
  props: IRightpanelFooterButtonProps;
}

export interface IRightpanelFooter {
  button?: IRightpanelFooterButton[];
  children?: JSXElement;
}

export interface RightpanelProps {
  header?: JSXElement; // Simple header content like section
  // Header navigation props
  headerTitle?: string;
  topLeftButton?: string;
  topRightPrimaryButton?: string;
  topRightSecondaryButton?: string;
  footer?: IRightpanelFooter;
  sectionProps?: Record<string, unknown>;
  bodyProps?: Record<string, unknown>;
  content?: JSXElement;
  // Callback props for event handling
  onFooterButtonClick?(
    button: IRightpanelFooterButton,
    event: MouseEvent
  ): void;
  onLeftButtonClick?(): void;
  onPrimaryButtonClick?(): void;
  onSecondaryButtonClick?(): void;
}

const Rightpanel: Component<RightpanelProps> = (props) => {
  let panelRef: HTMLElement | undefined;

  onMount(() => {
    let cancelled = false;

    // Wait until the Stencil custom element is fully upgraded before attempting
    // to access its shadow root. customElements.whenDefined() resolves at the
    // exact moment the element class is registered, at which point shadowRoot
    // is guaranteed to exist — no arbitrary retry count needed.
    customElements.whenDefined('modus-wc-utility-panel').then(() => {
      if (cancelled || !panelRef) return;
      injectInRenderContext(
        panelRef,
        'mc-rightpanel-style',
        MODUS_RIGHTPANEL_STYLESHEET
      );
    });

    onCleanup(() => {
      cancelled = true;
    });
  });

  const handleFooterButtonClick = (
    button: IRightpanelFooterButton,
    event: MouseEvent
  ): void => {
    // Execute original button props onClick if exists
    if (button.props?.onClick) {
      button.props.onClick(event);
    }

    // Call callback prop if provided
    if (props.onFooterButtonClick) {
      props.onFooterButtonClick(button, event);
    }
  };

  // Render footer buttons
  const renderFooterButtons = (): JSXElement => {
    const footer = props.footer;

    if (!footer || !footer.button) {
      return null;
    }

    const buttons = footer.button;

    return (
      <div class="flex gap-2 w-full">
        <For each={buttons}>
          {(button) => {
            const buttonClass = ['button', button.class];
            if (buttons.length < SINGLE_BUTTON_THRESHOLD) {
              buttonClass.push('full-width', 'mx-0');
            }

            return (
              <button
                class={buttonClass.join(' ')}
                {...button.props}
                onClick={(e) => {
                  handleFooterButtonClick(button, e);
                }}
              >
                {button.text}
              </button>
            );
          }}
        </For>
      </div>
    );
  };

  // Render header navigation
  const renderHeader = (): JSXElement => {
    if (props.header) {
      return props.header;
    }

    return (
      <div class="flex flex-row flex-nowrap items-center w-full min-w-0 gap-2">
        <Show when={props.topLeftButton}>
          <button
            type="button"
            class="button icon-medium icon-circle tertiary shrink-0"
            onClick={() => props.onLeftButtonClick?.()}
          >
            <i class={`icon-font ${props.topLeftButton ?? ''}`} />
          </button>
        </Show>
        <Show when={props.headerTitle}>
          <h3 class="header-title m-0 min-w-0 flex-1 truncate">
            {props.headerTitle}
          </h3>
        </Show>
        <div class="ml-auto flex flex-row flex-nowrap items-center gap-2 shrink-0">
          <Show when={props.topRightSecondaryButton}>
            <button
              type="button"
              class="button icon-medium icon-circle tertiary"
              onClick={() => props.onSecondaryButtonClick?.()}
            >
              <i class={`icon-font ${props.topRightSecondaryButton ?? ''}`} />
            </button>
          </Show>
          <Show when={props.topRightPrimaryButton}>
            <button
              type="button"
              class="button icon-medium icon-circle primary"
              onClick={() => props.onPrimaryButtonClick?.()}
            >
              <i class={`icon-font ${props.topRightPrimaryButton ?? ''}`} />
            </button>
          </Show>
        </div>
      </div>
    );
  };

  return (
    <modus-wc-utility-panel
      // @ts-expect-error: this is a valid ref
      ref={(el: HTMLElement) => {
        panelRef = el;
      }}
      expanded={true}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      <div slot="header">{renderHeader()}</div>
      <div slot="body" {...props.bodyProps}>
        {props.content}
      </div>
      <Show when={props.footer}>
        <div slot="footer" class={props.footer?.children ? 'p-0' : ''}>
          {props.footer?.children}
          <Show when={props.footer?.button}>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            <div class="px-2 py-2">{renderFooterButtons()}</div>
          </Show>
        </div>
      </Show>
    </modus-wc-utility-panel>
  );
};

export default Rightpanel;
